import CfiParser from 'cfi-parser';
import CfiUtils from 'cfi-utils';

class CfiGenerator {

  constructor(docNode, options) {
    if (docNode.documentElement) {
      this.doc = docNode;
    } else {
      throw new TypeError('The top level node must be a document.');
    }

    this.parser = new CfiParser(this.doc, options);
    this.utils = new CfiUtils(options);
  }

  getBySelection(selection) {
    if (!(selection.anchorNode && 'anchorOffset' in selection && selection.focusNode && 'focusOffset' in selection)) {
      throw new TypeError('The selection parameter must have anchorNode, anchorOffset, focusNode, and focusOffset.');
    }

    let anchorNode = selection.anchorNode;
    let anchorOffset = parseInt(selection.anchorOffset, 10);
    let focusNode = selection.focusNode;
    let focusOffset = parseInt(selection.focusOffset, 10);

    if (this.utils.isBlacklisted(anchorNode) ||
      this.utils.isBlacklisted(focusNode)) {
      throw new Error('The selection starts or ends in a blacklisted element. ' +
        'It should not be possible to select these elements.');
    }

    let positionComparison = anchorNode.compareDocumentPosition(focusNode);

    if (!positionComparison && anchorOffset > focusOffset || positionComparison === Node.DOCUMENT_POSITION_PRECEDING) {
      // selection is reversed, change anchor and focus.
      anchorNode = selection.focusNode;
      anchorOffset = selection.focusOffset;
      focusNode = selection.anchorNode;
      focusOffset = selection.anchorOffset;
    }

    return this._createCfiForNodes(anchorNode, anchorOffset, focusNode, focusOffset);
  }

  getByElement(node) {
    return this._createCfiStepsForElement(node);
  }

  mergeCfisToRange(firstCfi, secondCfi) {
    let sortedCfis = this.utils.sort([firstCfi, secondCfi]);
    let anchorCfi = sortedCfis[0];
    let focusCfi = sortedCfis[1];

    let anchor = this.parser.getElementByCfi(anchorCfi);
    let focus = this.parser.getElementByCfi(focusCfi);

    return this._createCfiForNodes(anchor.node, anchor.offset, focus.node, focus.offset);
  }

  _createCfiStepsForElement(element) {
    if (element.nodeType === Node.DOCUMENT_NODE || element.nodeName.toLowerCase() === 'html') {
      element = element.getElementsByTagName('body')[0];
    }

    if (element.nodeType !== Node.ELEMENT_NODE) {
      throw new TypeError('_createCfiStepsForElement only accepts element nodes.');
    }

    let currElementPosition = -1;
    let elementStep = '';
    let parentNode = element.parentNode;
    let children = [];

    for (let i = 0; i < parentNode.children.length; i++) {
      let node = parentNode.children[i];

      if (!(this.utils.isBlacklisted(node))) {
        children.push(node);
      }
    }

    for (let i = 0; i < children.length; i++) {
      if (children[i] === element) {
        currElementPosition = i;
        break;
      }
    }

    let cfiPosition = (currElementPosition + 1) * 2;

    if (element.id && element.id !== '') {
      elementStep = `/${cfiPosition}[${element.id}]`;
    } else {
      elementStep = `/${cfiPosition}`;
    }

    if (element === this.doc.body) {
      return elementStep;
    }

    return this._createCfiStepsForElement(parentNode) + elementStep;
  }

  _createCfiStepsForNode(node, characterOffset) {
    let hasOffset = !isNaN(characterOffset);
    let offset = hasOffset ? parseInt(characterOffset, 10) : 0;
    let originalOffset = offset;
    let parentNode = node.parentNode;
    let childNodes = [];

    // Calculate node step
    for (let i = 0; i < parentNode.childNodes.length; i++) {
      let childNode = parentNode.childNodes[i];
      let isTextNode = childNode.nodeType === Node.TEXT_NODE;
      let isBlacklistedNode = this.utils.isBlacklisted(childNode);

      if (i === 0) {
        if (isTextNode || isBlacklistedNode) {
          childNode.cfiNodeStep = 1;
        } else {
          childNode.cfiNodeStep = 2;
        }
      } else {
        let previousNodeSiblingStep = childNode.previousSibling.cfiNodeStep;
        let isPrevSiblingElementNode = childNode.previousSibling.nodeType === Node.ELEMENT_NODE;
        let isPrevSiblingTextNode = childNode.previousSibling.nodeType === Node.TEXT_NODE;
        let isPrevSiblingBlacklisted = this.utils.isBlacklisted(childNode.previousSibling);
        let isElementNode = childNode.nodeType === Node.ELEMENT_NODE;

        if ((isBlacklistedNode && isPrevSiblingTextNode) ||
          (isTextNode &&
            (isPrevSiblingTextNode || isPrevSiblingBlacklisted)) || isBlacklistedNode && isPrevSiblingBlacklisted) {
          // if blacklisted node's previous sibling is text/space then assign it to the previous sibling node step
          childNode.cfiNodeStep = previousNodeSiblingStep;
        } else if (isBlacklistedNode && isPrevSiblingElementNode ||
          isElementNode && isPrevSiblingBlacklisted) {
          // if blacklisted nodes previous sibling is an element then add one to the previous sibling node step
          childNode.cfiNodeStep = previousNodeSiblingStep + 1;
        } else if (isElementNode && isPrevSiblingElementNode) {
          childNode.cfiNodeStep = previousNodeSiblingStep + 2;
        } else {
          childNode.cfiNodeStep = previousNodeSiblingStep + 1;
        }
      }

      if (isBlacklistedNode) {
        childNode.cfiBlacklisted = true;
      }

      childNodes.push(childNode);
    }

    // Calculate character offset
    for (let i = 0; i < childNodes.length; i++) {
      let isBlacklistedNode = 'cfiBlacklisted' in childNodes[i] && childNodes[i].cfiBlacklisted;
      let isTextNode = childNodes[i].nodeType === Node.TEXT_NODE;
      let isPrevSiblingTextNode = childNodes[i].previousSibling &&
        childNodes[i].previousSibling.nodeType === Node.TEXT_NODE;

      if ((isBlacklistedNode || isTextNode) && isPrevSiblingTextNode) {
        offset += childNodes[i - 1].textContent.length;
      } else if (childNodes[i].nodeType === Node.ELEMENT_NODE && !isBlacklistedNode) {
        offset = originalOffset;
      }
      if (childNodes[i] === node) {
        break;
      }
    }

    if (hasOffset) {
      return `/${node.cfiNodeStep}:${offset}`;
    }

    return `/${node.cfiNodeStep}`;
  }

  _createCfiStepsForTextNode(node, characterOffset) {
    if (node.nodeType === Node.TEXT_NODE) {
      let parentNodeCfi = this._createCfiStepsForElement(node.parentNode);
      let nodeCfi = this._createCfiStepsForNode(node, characterOffset);

      return `${parentNodeCfi}${nodeCfi}`;
    }

    throw new TypeError('_createCfiStepsForTextNode only accepts text nodes.');
  }

  _createCfiForNodes(anchorNode, anchorOffset, focusNode, focusOffset) {
    let commonAncestor = null;
    let commonCfi = '';

    if (anchorNode === focusNode) {
      commonAncestor = anchorNode.parentNode;

      if (anchorNode.nodeType === Node.ELEMENT_NODE) {
        // if a single element is selected return the element cfi since a range is not possible.
        return this.getByElement(anchorNode);
      }
    } else {
      let docRange = document.createRange();

      docRange.setStart(anchorNode, anchorOffset);
      docRange.setEnd(focusNode, focusOffset);

      commonAncestor = docRange.commonAncestorContainer;
    }

    if (commonAncestor.nodeType === Node.TEXT_NODE) {
      commonCfi = this._createCfiStepsForTextNode(commonAncestor);
    }

    if (commonAncestor.nodeType === Node.ELEMENT_NODE) {
      commonCfi = this._createCfiStepsForElement(commonAncestor);
    }

    let anchorCfi = '';

    if (anchorNode.nodeType === Node.TEXT_NODE) {
      anchorCfi = this._createCfiStepsForTextNode(anchorNode, anchorOffset);
    }

    if (anchorNode.nodeType === Node.ELEMENT_NODE) {
      anchorCfi = this._createCfiStepsForElement(anchorNode);
    }

    let focusCfi = '';

    if (focusNode.nodeType === Node.TEXT_NODE) {
      focusCfi = this._createCfiStepsForTextNode(focusNode, focusOffset);
    }

    if (focusNode.nodeType === Node.ELEMENT_NODE) {
      focusCfi = this._createCfiStepsForElement(focusNode);
    }

    anchorCfi = anchorCfi.replace(commonCfi, '');
    focusCfi = focusCfi.replace(commonCfi, '');

    return `${commonCfi},${anchorCfi},${focusCfi}`;
  }
}

export default CfiGenerator;



// WEBPACK FOOTER //
// ./src/cfi-generator.js